import React, { useEffect } from "react";
import { Grid, Select, Typography, MenuItem, Tooltip } from "@material-ui/core";
import { gql } from "graphql-tag";
import { orderBy } from "lodash";
import { useQuery } from "@apollo/client";
import { Link } from "@reach/router";
import { Alert } from "@material-ui/lab";
import { Info as InfoIcon } from "@material-ui/icons";
import { Timezone } from "pages/dashboard/admin/import/interest_imports/types";
import { getListByIdShort, getListByIdShortVariables } from "types/gql-types";
import { StepData, StepProps } from "..";
import { useStepData } from "../context";
import StepLayout from "./layout";
import { validateStep } from "./validation";


export const INTEREST_LIST = gql`
  query getListByIdShort($id: ID!) {
    interestList(id: $id) {
      id
      name
      property {
        timezone
      }
    }
  }
`;

const toKabobCase = (str: string) => str.replace(/\s+/g, "-").toLowerCase();


const TimezoneAutoSelect: React.FC<Omit<StepProps, "viewer">> = () => {
  const { stepData, setStepData } = useStepData();
  if (!stepData.listId) return null;

  const linkToSettings = `/dash/manager/settings/property/${stepData.listId}/lists`;

  const { data, loading } = useQuery<getListByIdShort, getListByIdShortVariables>(INTEREST_LIST, {
    fetchPolicy: "cache-first",
    variables: { id: stepData.listId },
  });

  useEffect(() => {
    if (data && data.interestList) {
      setStepData((oldData) => ({
        ...oldData,
        timezone: (data.interestList.property.timezone || "none") as Timezone,
      }));
    }
  }, [data, setStepData]);

  if (loading) return <Typography>Loading...</Typography>;
  return <>
    {stepData.timezone && stepData.timezone !== "none" ? <Typography variant="body1">
      <strong>Time Zone of applications:</strong>
      {" "}{stepData.timezone}<Tooltip title="Edit Time Zone in Settings">
        <Link to={linkToSettings}>
          <InfoIcon fontSize="small" viewBox="0 0 30 30" />
        </Link>
      </Tooltip>
    </Typography> : <>
      <Alert severity="error">
        Timezone not set for this list.
      </Alert>
      <Typography variant="body1">
        <Link to={linkToSettings}>Set Timezone</Link>
      </Typography>
    </>}
  </>;
};

const StepOrganize: React.FC<StepProps> = () => {
  const { stepData, setStepData, viewer, setStepName } = useStepData();
  if (!validateStep(stepData, "organize")) {
    return null;
  }
  const fileName = stepData.file.name;

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setStepData((prevData: StepData) => ({ ...prevData, listId: `${event.target.value}` }));
  };

  // if only one list, set it as default
  useEffect(
    () => {
      if (viewer.lists && viewer.lists.length === 1) {
        const [firstList] = viewer.lists;
        setStepData((prevData: StepData) => ({ ...prevData, listId: firstList.id }));
      }
    }
    , [viewer.lists, setStepData],
  );

  useEffect(() => {
    setStepName("Organize");
  }, []);


  return <StepLayout title="Organize your applications"
    description={<Typography variant="body1">
    Where should the applications from <strong>{fileName}</strong> be added?
    </Typography>}>

    <Grid item xs={12}>
      <Typography variant="body1">
        Current Waitlist
      </Typography>

      <Select
        data-cy="list-selector"
        fullWidth
        margin="dense"
        variant="outlined"
        onChange={handleChange}
        value={stepData.listId}>
        <MenuItem disabled value="">Select a list</MenuItem>
        {viewer.lists &&
            viewer.lists.length &&
            orderBy(viewer.lists, "name", "asc").map((list, i) => (
              <MenuItem value={list.id} key={i} data-cy={`list-${toKabobCase(list.name)}`}>
                {list.name}
              </MenuItem>
            ))}
      </Select>
    </Grid>
    <Grid item xs={12}>
      {stepData.listId && <TimezoneAutoSelect />}
    </Grid>
    <Grid item xs={12}>
      <Typography variant="body1">
        The data from your import will <strong>not</strong> update any existing applications. If any of these applications already exist, duplicates will be imported.
      </Typography>
    </Grid>
  </StepLayout>;
};

export default StepOrganize;
